import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API } from '@/App';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { ArrowLeft, Plus, Edit, Trash2, Shield, X, Upload, FileText } from 'lucide-react';

const ServiceManagement = () => {
  const navigate = useNavigate();
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingService, setEditingService] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    icon: 'TrendingUp',
    color: 'from-blue-600 to-blue-800',
    features: [],
    contact_name: 'NOK Thailand Team',
    contact_phone: '',
    image_url: '',
    documents: [],
    order: 0,
    published: true
  });
  const [newFeature, setNewFeature] = useState('');
  const [newDocument, setNewDocument] = useState({ name: '', url: '' });

  const iconOptions = [
    'TrendingUp', 'Shield', 'Heart', 'Scale', 'Building2', 'Users', 
    'FileText', 'Briefcase', 'Award', 'Globe'
  ];

  const colorOptions = [
    { label: 'Blue', value: 'from-blue-600 to-blue-800' },
    { label: 'Green', value: 'from-green-600 to-emerald-800' },
    { label: 'Orange', value: 'from-amber-600 to-orange-800' },
    { label: 'Purple', value: 'from-purple-600 to-purple-800' },
    { label: 'Red', value: 'from-red-600 to-red-800' },
    { label: 'Teal', value: 'from-teal-600 to-teal-800' }
  ];

  useEffect(() => {
    fetchServices();
  }, []);

  const fetchServices = async () => {
    try {
      const response = await axios.get(`${API}/services?published_only=false`);
      setServices(response.data);
    } catch (error) {
      toast.error('Failed to fetch services');
    } finally {
      setLoading(false);
    }
  };

  const openCreateDialog = () => {
    setEditingService(null);
    setFormData({
      title: '',
      description: '',
      icon: 'TrendingUp',
      color: 'from-blue-600 to-blue-800',
      features: [],
      contact_name: 'NOK Thailand Team',
      contact_phone: '',
      image_url: '',
      documents: [],
      order: 0,
      published: true
    });
    setDialogOpen(true);
  };

  const openEditDialog = (service) => {
    setEditingService(service);
    setFormData({
      title: service.title,
      description: service.description,
      icon: service.icon,
      color: service.color,
      features: service.features || [],
      contact_name: service.contact_name,
      contact_phone: service.contact_phone,
      image_url: service.image_url || '',
      documents: service.documents || [],
      order: service.order,
      published: service.published
    });
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!formData.title || !formData.description) {
      toast.error('Title and description are required');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      
      if (editingService) {
        await axios.put(`${API}/services/${editingService.id}`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Service updated successfully');
      } else {
        await axios.post(`${API}/services`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Service created successfully');
      }
      
      setDialogOpen(false);
      fetchServices();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save service');
    }
  };

  const handleDelete = async (serviceId) => {
    if (!window.confirm('Are you sure you want to delete this service?')) return;
    
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/services/${serviceId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Service deleted successfully');
      fetchServices();
    } catch (error) {
      toast.error('Failed to delete service');
    }
  };

  const addFeature = () => {
    if (newFeature.trim()) {
      setFormData({
        ...formData,
        features: [...formData.features, newFeature.trim()]
      });
      setNewFeature('');
    }
  };

  const removeFeature = (index) => {
    setFormData({
      ...formData,
      features: formData.features.filter((_, i) => i !== index)
    });
  };

  const addDocument = () => {
    if (newDocument.name.trim() && newDocument.url.trim()) {
      setFormData({
        ...formData,
        documents: [...formData.documents, { ...newDocument }]
      });
      setNewDocument({ name: '', url: '' });
    }
  };

  const removeDocument = (index) => {
    setFormData({
      ...formData,
      documents: formData.documents.filter((_, i) => i !== index)
    });
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#0A0E27] via-[#1a1f3a] to-[#0A0E27]">
      <header className="bg-black/40 backdrop-blur-md border-b border-[#D4AF37]/20 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="font-display text-2xl font-bold text-[#D4AF37]">Service Management</h1>
              <p className="text-[#C9A961] text-sm">Insurance, Legal & Advisory Services</p>
            </div>
            <div className="flex gap-3">
              <Button
                onClick={openCreateDialog}
                className="bg-gradient-to-r from-[#D4AF37] to-[#C9A961] hover:from-[#C9A961] hover:to-[#D4AF37] text-black font-semibold"
              >
                <Plus className="w-4 h-4 mr-2" />
                New Service
              </Button>
              <Button 
                onClick={() => navigate('/admin/dashboard')} 
                variant="outline"
                className="border-[#D4AF37] text-[#D4AF37] hover:bg-[#D4AF37] hover:text-black"
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Dashboard
              </Button>
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {loading ? (
          <div className="text-center py-20">
            <div className="inline-block h-12 w-12 animate-spin rounded-full border-4 border-solid border-[#D4AF37] border-r-transparent"></div>
          </div>
        ) : (
          <div className="grid md:grid-cols-2 gap-6">
            {services.map((service) => (
              <div key={service.id} className="bg-gradient-to-br from-black/70 to-[#1a1f3a]/70 backdrop-blur-lg rounded-xl border-2 border-[#D4AF37]/30 overflow-hidden">
                <div className={`bg-gradient-to-r ${service.color} p-6`}>
                  <div className="flex items-center justify-between">
                    <h3 className="font-display text-xl font-bold text-white">{service.title}</h3>
                    {!service.published && (
                      <span className="px-2 py-1 bg-gray-500 text-white text-xs font-bold rounded">Draft</span>
                    )}
                  </div>
                </div>
                <div className="p-6">
                  <p className="text-gray-300 text-sm mb-4 line-clamp-3">{service.description}</p>
                  <div className="mb-4">
                    <p className="text-xs text-gray-500 mb-2">Features: {service.features?.length || 0}</p>
                    <p className="text-xs text-gray-500">Documents: {service.documents?.length || 0}</p>
                    <p className="text-xs text-gray-500">Contact: {service.contact_name}</p>
                  </div>
                  <div className="flex gap-2">
                    <Button
                      onClick={() => openEditDialog(service)}
                      variant="outline"
                      size="sm"
                      className="flex-1 border-[#D4AF37] text-[#D4AF37] hover:bg-[#D4AF37] hover:text-black"
                    >
                      <Edit className="w-4 h-4 mr-1" />
                      Edit
                    </Button>
                    <Button
                      onClick={() => handleDelete(service.id)}
                      variant="outline"
                      size="sm"
                      className="border-red-500 text-red-500 hover:bg-red-500 hover:text-white"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {!loading && services.length === 0 && (
          <div className="text-center py-20 bg-black/40 backdrop-blur-lg rounded-2xl border-2 border-[#D4AF37]/30">
            <Shield className="w-16 h-16 mx-auto text-[#D4AF37] mb-4" />
            <p className="text-xl text-gray-300 mb-4">No services yet</p>
            <Button onClick={openCreateDialog} className="bg-gradient-to-r from-[#D4AF37] to-[#C9A961] text-black">
              Create Your First Service
            </Button>
          </div>
        )}
      </div>

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl text-[#D4AF37]">
              {editingService ? 'Edit Service' : 'Create New Service'}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Title *</Label>
                <Input
                  value={formData.title}
                  onChange={(e) => setFormData({...formData, title: e.target.value})}
                  placeholder="e.g., Insurance & Investment Services"
                />
              </div>
              <div>
                <Label>Display Order</Label>
                <Input
                  type="number"
                  value={formData.order}
                  onChange={(e) => setFormData({...formData, order: parseInt(e.target.value)})}
                />
              </div>
            </div>

            <div>
              <Label>Description *</Label>
              <Textarea
                value={formData.description}
                onChange={(e) => setFormData({...formData, description: e.target.value})}
                placeholder="Detailed service description"
                rows={4}
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Icon</Label>
                <Select value={formData.icon} onValueChange={(value) => setFormData({...formData, icon: value})}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {iconOptions.map(icon => (
                      <SelectItem key={icon} value={icon}>{icon}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Color Theme</Label>
                <Select value={formData.color} onValueChange={(value) => setFormData({...formData, color: value})}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {colorOptions.map(opt => (
                      <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Features */}
            <div>
              <Label>Features/Services Included</Label>
              <div className="flex gap-2 mb-2">
                <Input
                  value={newFeature}
                  onChange={(e) => setNewFeature(e.target.value)}
                  placeholder="Add a feature"
                  onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addFeature())}
                />
                <Button onClick={addFeature} type="button" variant="outline">
                  <Plus className="w-4 h-4" />
                </Button>
              </div>
              <div className="space-y-1">
                {formData.features.map((feature, idx) => (
                  <div key={idx} className="flex items-center gap-2 bg-gray-100 p-2 rounded">
                    <span className="flex-1 text-sm">{feature}</span>
                    <button onClick={() => removeFeature(idx)} className="text-red-500">
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                ))}
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Contact Name</Label>
                <Input
                  value={formData.contact_name}
                  onChange={(e) => setFormData({...formData, contact_name: e.target.value})}
                />
              </div>
              <div>
                <Label>Contact Phone</Label>
                <Input
                  value={formData.contact_phone}
                  onChange={(e) => setFormData({...formData, contact_phone: e.target.value})}
                  placeholder="e.g., 081-941-5945"
                />
              </div>
            </div>

            <div>
              <Label>Image URL or Upload</Label>
              <Input
                value={formData.image_url}
                onChange={(e) => setFormData({...formData, image_url: e.target.value})}
                placeholder="Paste image URL or use upload button"
                className="mb-2"
              />
              <div className="text-sm text-gray-600">
                <p className="mb-2">💡 To upload images:</p>
                <ol className="list-decimal list-inside space-y-1 text-xs">
                  <li>Use free image hosting: <a href="https://imgur.com" target="_blank" className="text-blue-600 underline">Imgur</a>, <a href="https://postimages.org" target="_blank" className="text-blue-600 underline">PostImages</a></li>
                  <li>Upload your image there</li>
                  <li>Copy the direct image URL</li>
                  <li>Paste it above</li>
                </ol>
              </div>
            </div>

            {/* Documents */}
            <div>
              <Label>Documents (PDF, Guides, etc.)</Label>
              <div className="grid grid-cols-2 gap-2 mb-2">
                <Input
                  value={newDocument.name}
                  onChange={(e) => setNewDocument({...newDocument, name: e.target.value})}
                  placeholder="Document name"
                />
                <div className="flex gap-2">
                  <Input
                    value={newDocument.url}
                    onChange={(e) => setNewDocument({...newDocument, url: e.target.value})}
                    placeholder="Document URL"
                  />
                  <Button onClick={addDocument} type="button" variant="outline" className="whitespace-nowrap">
                    <Plus className="w-4 h-4" />
                  </Button>
                </div>
              </div>
              <div className="space-y-1">
                {formData.documents.map((doc, idx) => (
                  <div key={idx} className="flex items-center gap-2 bg-gray-100 p-2 rounded">
                    <FileText className="w-4 h-4 text-gray-600" />
                    <span className="flex-1 text-sm">{doc.name}</span>
                    <button onClick={() => removeDocument(idx)} type="button" className="text-red-500">
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                ))}
              </div>
            </div>

            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={formData.published}
                onChange={(e) => setFormData({...formData, published: e.target.checked})}
                className="h-4 w-4"
              />
              <Label>Published (visible to public)</Label>
            </div>

            <div className="flex gap-3 pt-4">
              <Button onClick={handleSave} className="flex-1 bg-gradient-to-r from-[#D4AF37] to-[#C9A961] text-black">
                {editingService ? 'Update Service' : 'Create Service'}
              </Button>
              <Button onClick={() => setDialogOpen(false)} variant="outline" className="flex-1">
                Cancel
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ServiceManagement;
